export const MIN_AUTOCOMPLETE_CHARS = 3;
export const AUTOCOMPLETE_DELAY_MS = 300;

export const MOCK_ATHENA_CONCEPTS = [
  // Diagnoses (lung cancer focused + general)
  { id: 1, name: 'Non-Small Cell Lung Cancer', category: 'Diagnoses', llmSuggested: true },
  { id: 2, name: 'Small Cell Lung Cancer', category: 'Diagnoses', llmSuggested: true },
  { id: 3, name: 'Lung Adenocarcinoma', category: 'Diagnoses', llmSuggested: false },
  { id: 4, name: 'Squamous Cell Carcinoma (Lung)', category: 'Diagnoses', llmSuggested: false },
  { id: 5, name: 'Tumor Stage', category: 'Diagnoses', llmSuggested: false },
  { id: 6, name: 'COPD', category: 'Diagnoses', llmSuggested: false },
  { id: 7, name: 'Hypertension', category: 'Diagnoses', llmSuggested: false },

  // Labs
  { id: 20, name: 'EGFR Mutation', category: 'Labs', llmSuggested: true },
  { id: 21, name: 'PD-L1 Expression', category: 'Labs', llmSuggested: true },
  { id: 22, name: 'ALK Rearrangement', category: 'Labs', llmSuggested: false },
  { id: 23, name: 'Hemoglobin', category: 'Labs', llmSuggested: false },
  { id: 24, name: 'Creatinine', category: 'Labs', llmSuggested: false },
  { id: 25, name: 'ALT (SGPT)', category: 'Labs', llmSuggested: false },
  { id: 26, name: 'C-Reactive Protein', category: 'Labs', llmSuggested: false },

  // Vitals
  { id: 40, name: 'Systolic Blood Pressure', category: 'Vitals', llmSuggested: false },
  { id: 41, name: 'Diastolic Blood Pressure', category: 'Vitals', llmSuggested: false },
  { id: 42, name: 'Heart Rate', category: 'Vitals', llmSuggested: false },
  { id: 43, name: 'Respiratory Rate', category: 'Vitals', llmSuggested: false },
  { id: 44, name: 'SpO2', category: 'Vitals', llmSuggested: false },
  { id: 45, name: 'Body Temperature', category: 'Vitals', llmSuggested: false },

  // Medications
  { id: 60, name: 'Pembrolizumab', category: 'Medications', llmSuggested: true },
  { id: 61, name: 'Nivolumab', category: 'Medications', llmSuggested: true },
  { id: 62, name: 'Cisplatin', category: 'Medications', llmSuggested: false },
  { id: 63, name: 'Carboplatin', category: 'Medications', llmSuggested: false },
  { id: 64, name: 'Paclitaxel', category: 'Medications', llmSuggested: false },
  { id: 65, name: 'Osimertinib', category: 'Medications', llmSuggested: true },

  // Demographics
  { id: 80, name: 'Age', category: 'Demographics', llmSuggested: false },
  { id: 81, name: 'Gender', category: 'Demographics', llmSuggested: false },
  { id: 82, name: 'Smoking Status', category: 'Demographics', llmSuggested: true },
  { id: 83, name: 'Pack Years', category: 'Demographics', llmSuggested: true },
  { id: 84, name: 'ECOG Performance Status', category: 'Demographics', llmSuggested: false },
];

const normalize = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const compact = (value) => normalize(value).replace(/\s+/g, '');

const initials = (value) =>
  normalize(value)
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('');

const isSubsequence = (needle, haystack) => {
  if (!needle) return true;
  let i = 0;
  let j = 0;
  while (i < needle.length && j < haystack.length) {
    if (needle[i] === haystack[j]) i += 1;
    j += 1;
  }
  return i === needle.length;
};

const scoreMatch = (query, conceptName) => {
  const qNorm = normalize(query);
  const nNorm = normalize(conceptName);
  if (!qNorm || !nNorm) return -1;

  const qCompact = compact(qNorm);
  const nCompact = compact(nNorm);
  const nInitials = initials(nNorm);

  if (nNorm.startsWith(qNorm)) return 140;
  if (nCompact.startsWith(qCompact)) return 130;
  if (nNorm.includes(qNorm)) return 110;
  if (nInitials.includes(qCompact)) return 95;
  if (isSubsequence(qCompact, nCompact)) return 80;
  return -1;
};

const hashToUnit = (value) => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967295;
};

const resolveLlmSuggested = (concept, query) => {
  if (concept.llmSuggested) return true;
  const sample = hashToUnit(`${query.toLowerCase()}::${concept.id}`);
  return sample < 0.26;
};

export const searchMockAthenaConcepts = (query, { limit = 20 } = {}) => {
  const raw = String(query || '').trim();
  if (raw.length < MIN_AUTOCOMPLETE_CHARS) return [];

  const matches = MOCK_ATHENA_CONCEPTS.map((concept) => {
    const score = scoreMatch(raw, concept.name);
    if (score < 0) return null;
    return {
      ...concept,
      llmSuggested: resolveLlmSuggested(concept, raw),
      _score: score,
    };
  }).filter(Boolean);

  matches.sort((a, b) => {
    if (b._score !== a._score) return b._score - a._score;
    return a.name.localeCompare(b.name);
  });

  return matches.slice(0, limit);
};
